import { useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, CalendarClock, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { capi } from "@/lib/customer";

const SLOTS = ["09:00", "10:30", "12:00", "14:00", "15:30", "17:00"];
const TOPICS = ["Global mobility", "Relocation", "Tax optimisation", "Business setup", "Children's future"];

const todayPlus = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

export default function BookingModal({ open, onClose, journey, consultant }) {
  const [date, setDate] = useState(todayPlus(1));
  const [time, setTime] = useState(SLOTS[1]);
  const [topic, setTopic] = useState(TOPICS[0]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await capi.post("/customer/bookings", { date, time, topic, note: note || null, journey_id: journey?.id || null });
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not request your session");
    } finally { setBusy(false); }
  };

  const name = consultant?.name || "your specialist";

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
          <motion.div
            data-testid="booking-modal"
            className="fixed inset-x-0 bottom-0 z-[81] mx-auto max-w-lg rounded-t-3xl border-t border-white/10"
            style={{ background: "rgba(10,14,23,0.99)", boxShadow: "0 -20px 60px rgba(0,0,0,0.6)" }}
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 300 }}
          >
            <div className="flex justify-center pt-3"><div className="h-1.5 w-12 rounded-full bg-white/20" /></div>
            <div className="flex items-start justify-between px-5 pt-3">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400/90">Private Session</p>
                <h2 className="mt-1 text-xl font-semibold text-white" style={{ fontFamily: "'Syne', sans-serif" }}>Book 30 minutes with {name.split(" ")[0]}</h2>
              </div>
              <button data-testid="booking-close" onClick={onClose} className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-white/10 text-slate-300"><X size={16} /></button>
            </div>

            <div className="px-5 pb-7 pt-4">
              {done ? (
                <div data-testid="booking-success" className="flex flex-col items-center py-6 text-center">
                  <div className="grid h-16 w-16 place-items-center rounded-2xl bg-emerald-400/15 text-emerald-300"><CheckCircle2 size={30} /></div>
                  <h3 className="mt-4 text-lg font-semibold text-white">Session requested</h3>
                  <p className="mt-1 max-w-xs text-sm text-slate-300">{name} will confirm your <strong className="text-amber-200">{date} at {time}</strong> slot shortly. A confirmation is on its way to your inbox.</p>
                  <button data-testid="booking-done" onClick={onClose} className="mt-5 rounded-full bg-amber-300 px-6 py-2.5 text-sm font-semibold text-[#07090E]">Done</button>
                </div>
              ) : (
                <>
                  <p className="text-xs text-slate-300">A private conversation — no pitch, no programme list. Pick a time that suits you.</p>

                  <label className="mt-4 block text-[11px] font-medium uppercase tracking-wider text-slate-400">Preferred date</label>
                  <input data-testid="booking-date" type="date" min={todayPlus(0)} value={date} onChange={(e) => setDate(e.target.value)}
                    className="mt-1.5 w-full rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-2.5 text-sm text-white outline-none focus:border-amber-300/40" />

                  <label className="mt-4 block text-[11px] font-medium uppercase tracking-wider text-slate-400">Preferred time</label>
                  <div className="mt-1.5 grid grid-cols-3 gap-2">
                    {SLOTS.map((s) => (
                      <button key={s} data-testid={`booking-slot-${s}`} onClick={() => setTime(s)}
                        className="rounded-lg border py-2 text-xs font-semibold transition-colors"
                        style={{ borderColor: time === s ? "#E2C275" : "rgba(255,255,255,0.1)", background: time === s ? "rgba(226,194,117,0.15)" : "transparent", color: time === s ? "#E2C275" : "#94A3B8" }}>
                        {s}
                      </button>
                    ))}
                  </div>

                  <label className="mt-4 block text-[11px] font-medium uppercase tracking-wider text-slate-400">What are you exploring?</label>
                  <select data-testid="booking-topic" value={topic} onChange={(e) => setTopic(e.target.value)}
                    className="mt-1.5 w-full rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-2.5 text-sm text-white outline-none focus:border-amber-300/40">
                    {TOPICS.map((t) => <option key={t} value={t} className="text-slate-900">{t}</option>)}
                  </select>

                  <label className="mt-4 block text-[11px] font-medium uppercase tracking-wider text-slate-400">Anything to add (optional)</label>
                  <textarea data-testid="booking-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                    className="mt-1.5 w-full rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-2.5 text-sm text-white outline-none focus:border-amber-300/40" />

                  <button data-testid="booking-submit" onClick={submit} disabled={busy}
                    className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-amber-300 py-3.5 text-sm font-semibold text-[#07090E] active:scale-[0.98] disabled:opacity-60">
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <CalendarClock size={16} />}
                    {busy ? "Requesting…" : "Request my session"}
                  </button>
                  <p className="mt-2 text-center text-[11px] text-slate-500">100% confidential. We work with a limited number of families each month.</p>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
